"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Sparkles,
  Crown,
  Briefcase,
  Building2,
  Target,
  GraduationCap,
  Mic,
  PenLine,
  BookOpen,
  Compass,
  ShieldCheck,
  Stethoscope,
  Heart,
  Users,
  Layers,
  Activity,
  Brain,
  Lock,
  ArrowRight,
  Newspaper,
  Award,
  GitBranch,
  Scale,
  Globe2,
  TrendingUp,
  Network,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  platinum: { text: "text-platinum", bg: "from-platinum/10", border: "border-platinum/30", ring: "ring-platinum/20", pill: "glass-tinted-platinum border-platinum/20" },
  silver: { text: "text-silver", bg: "from-silver/10", border: "border-silver/30", ring: "ring-silver/20", pill: "glass-tinted-silver border-silver/20" },
  obsidian: { text: "text-obsidian", bg: "from-obsidian/10", border: "border-obsidian/30", ring: "ring-obsidian/20", pill: "glass-tinted-obsidian border-obsidian/20" },
  crimson: { text: "text-crimson", bg: "from-crimson/10", border: "border-crimson/30", ring: "ring-crimson/20", pill: "glass-tinted-crimson border-crimson/20" },
};

const HEADLINE_STATS = [
  { metric: "10", suffix: "", label: "Engagement Types", sub: "One operating method", accent: "red" },
  { metric: "6", suffix: "", label: "Service Modalities", sub: "Connected engagement lifecycle", accent: "purple" },
  { metric: "18", suffix: "", label: "Countries Served", sub: "Three continents", accent: "cyan" },
  { metric: "0", suffix: "", label: "Security Incidents", sub: "15+ years clean record", accent: "emerald" },
  { metric: "100", suffix: "%", label: "Client Satisfaction", sub: "Outcome accountability", accent: "amber-gold" },
  { metric: "15", suffix: "+", label: "Years of Operations", sub: "Verified sovereign delivery", accent: "denim" },
];

const PHILOSOPHY_PILLARS = [
  {
    icon: Stethoscope,
    title: "Diagnose Before Prescribing",
    body: "Every engagement begins with understanding the situation — the context, the stakes, the existing capabilities, the gaps. No engagement is prescribed without diagnosis. The clinical discipline that governs medical practice governs every service offered.",
    accent: "red",
  },
  {
    icon: Layers,
    title: "Systems Over Decks",
    body: "The deliverable is an operating system — protocols, checklists, decision frameworks, and the discipline to implement them. Not a strategy document. Not a presentation. A system that makes excellence the default outcome, not the exceptional one.",
    accent: "purple",
  },
  {
    icon: Target,
    title: "Outcome Accountability",
    body: "The engagement is measured by outcomes, not effort. The same accountability that governs medical practice — the patient either recovers or does not — governs every service delivered. Verified results, not activity reports.",
    accent: "cyan",
  },
  {
    icon: Lock,
    title: "Discretion as Foundation",
    body: "National security level confidentiality governs every engagement. Binding agreements, compartmentalized operations, zero information leakage. Discretion is not a policy preference — it is the structural foundation of how every engagement operates.",
    accent: "emerald",
  },
  {
    icon: Network,
    title: "Integration Over Isolation",
    body: "Each engagement type connects to the others — speaking introduces the method, advisory embeds it, mentorship personalizes it, workshops operationalize it, consulting transforms entire systems. The portfolio is a system, not a list.",
    accent: "amber-gold",
  },
  {
    icon: Brain,
    title: "Evidence Over Intuition",
    body: "Every recommendation is grounded in verified operational data — not theory, not best practices, not industry benchmarks. The methods have been tested across sovereign-scale deployments where the margin for error is zero.",
    accent: "denim",
  },
];

const MODALITIES = [
  {
    icon: Mic,
    title: "Public Authority",
    body: "Operational authority delivered from the stage. Keynotes and university lectures that translate the operating method into implementable insights for executive audiences, conferences, and academic programs.",
    accent: "red",
    services: ["Keynote Speaking", "University Lectures", "Press Commentary"],
  },
  {
    icon: Briefcase,
    title: "Strategic Counsel",
    body: "Embedding the operating method within leadership teams. Executive advisory and board service that brings sovereign-scale perspective to governance, decision-making, and operational architecture.",
    accent: "purple",
    services: ["Executive Advisory", "Board Service"],
  },
  {
    icon: Activity,
    title: "Operational Transformation",
    body: "Sovereign-scale operational redesign. Consulting and workshops that build the protocols, checklists, and accountability structures that produce consistent, measurable outcomes at the highest levels of consequence.",
    accent: "cyan",
    services: ["Operational Consulting", "Corporate Workshops"],
  },
  {
    icon: Users,
    title: "Executive Mentorship",
    body: "One-on-one leadership development. The direct transfer of the operating method to executives, founders, and emerging leaders preparing for sovereign-scale challenges — structured, accountable, and outcome-focused.",
    accent: "emerald",
    services: ["Executive Mentorship"],
  },
  {
    icon: PenLine,
    title: "Knowledge & Thought Leadership",
    body: "Written documentation of the operating method. Biweekly publication, flagship project on women in leadership, and longer essays that distribute the operating method across organizations before direct engagement.",
    accent: "amber-gold",
    services: ["Writing & Thought Leadership"],
  },
  {
    icon: ShieldCheck,
    title: "Sovereign Engagements",
    body: "Discreet, high-stakes engagements for the most demanding clients. By introduction only — national security level confidentiality, sovereign-grade operational interventions, and absolute discretion as the structural foundation.",
    accent: "denim",
    services: ["Private Engagements"],
  },
];

const ENGAGEMENT_TYPES = [
  {
    icon: Mic,
    title: "Keynote Speaking",
    href: "/services/keynote-speaking/",
    body: "Six signature topics, each grounded in a decade of sovereign-scale operations. Not theory — operational authority delivered from the stage.",
    accent: "red",
  },
  {
    icon: Briefcase,
    title: "Executive Advisory",
    href: "/services/executive-advisory/",
    body: "Ongoing operational counsel for C-suite executives and leadership teams. The operating method embedded in daily organizational practice.",
    accent: "purple",
  },
  {
    icon: Building2,
    title: "Board Service",
    href: "/services/board-service/",
    body: "Sovereign-grade governance oversight. Operational excellence perspective, cross-domain insight, and the 70% Operating Model at the board level.",
    accent: "cyan",
  },
  {
    icon: Users,
    title: "Executive Mentorship",
    href: "/services/executive-mentorship/",
    body: "One-on-one leadership development. The direct transfer of an operating method tested across sovereign-scale deployments.",
    accent: "emerald",
  },
  {
    icon: Activity,
    title: "Operational Consulting",
    href: "/services/operational-consulting/",
    body: "Sovereign-scale operational transformation. The implementation of the operating method at the organizational level — verified outcomes, not recommendations.",
    accent: "amber-gold",
  },
  {
    icon: Newspaper,
    title: "Press & Media Commentary",
    href: "/services/press-media-commentary/",
    body: "Expert perspective for journalists and media organizations. Operational intelligence delivered in accessible form — substance over soundbites.",
    accent: "denim",
  },
  {
    icon: GitBranch,
    title: "Corporate Workshops",
    href: "/services/corporate-workshops/",
    body: "Intensive team training programs that translate the clinical discipline into practical, implementable team practices — operating systems, not slide decks.",
    accent: "lime",
  },
  {
    icon: GraduationCap,
    title: "University Lectures",
    href: "/services/university-lectures/",
    body: "Academic lectures and guest sessions at universities, business schools, and executive education programs — operational education for the next generation.",
    accent: "coral",
  },
  {
    icon: BookOpen,
    title: "Writing & Thought Leadership",
    href: "/services/writing-thought-leadership/",
    body: "Biweekly publication, flagship project, and longer essays documenting the operating method — distributed advisory that reaches organizations before direct engagement.",
    accent: "platinum",
  },
  {
    icon: Lock,
    title: "Private Engagements",
    href: "/services/private-engagements/",
    body: "Sovereign-level engagements by introduction only. National security level confidentiality, compartmentalized operations, absolute discretion.",
    accent: "crimson",
  },
];

const ENGAGEMENT_MODELS = [
  {
    num: "01",
    title: "Retainer Model",
    body: "Ongoing advisory and operational counsel through regular sessions — weekly or biweekly. Designed for leadership teams seeking sustained implementation of the operating method as a discipline, not a project.",
    accent: "red",
  },
  {
    num: "02",
    title: "Project Model",
    body: "Defined engagements with specific deliverables — operational assessments, governance transformations, crisis response programs. Concludes when the deliverable is verified and the outcome is measured.",
    accent: "purple",
  },
  {
    num: "03",
    title: "Event Model",
    body: "Speaking engagements, workshops, and lectures. Designed for conferences, corporate events, educational programs, and private gatherings where the operating method is delivered through live engagement.",
    accent: "cyan",
  },
  {
    num: "04",
    title: "Intensive Model",
    body: "Concentrated, high-impact engagement over compressed timeframes. Designed for organizations facing urgent operational challenges, critical transitions, or crisis conditions where the operating method must be deployed rapidly.",
    accent: "emerald",
  },
  {
    num: "05",
    title: "Institutional Model",
    body: "University lectures, academic partnerships, and executive education programs. Designed for educational institutions seeking to integrate the operating method into their curricula — guest lectures to semester-long engagements.",
    accent: "amber-gold",
  },
  {
    num: "06",
    title: "Introduction Model",
    body: "Private engagements for sovereign-level clients. Access by introduction only, ensuring the diagnostic conversation can proceed with the trust and discretion that sovereign-level work demands.",
    accent: "denim",
  },
];

const PROCESS_STAGES = [
  {
    num: "01",
    title: "Inquiry",
    body: "The engagement begins with an inquiry — specifying the type of engagement, the organizational context, and the desired outcomes. Every inquiry receives a response grounded in operational discipline.",
    accent: "red",
  },
  {
    num: "02",
    title: "Diagnosis",
    body: "A preliminary conversation — typically 30 to 60 minutes — to understand the situation in depth. The diagnostic phase maps the organizational landscape with clinical precision: context, stakes, capabilities, gaps, root causes.",
    accent: "purple",
  },
  {
    num: "03",
    title: "Prescription",
    body: "Based on the diagnosis, the appropriate engagement type and structure are recommended. The prescription specifies the scope, format, duration, milestones, and outcome metrics — grounded in diagnostic data.",
    accent: "cyan",
  },
  {
    num: "04",
    title: "Engagement",
    body: "The engagement proceeds according to the agreed model. Every session is grounded in the clinical method — diagnose, prescribe, implement, measure. The engagement produces measurable progress at every stage.",
    accent: "emerald",
  },
  {
    num: "05",
    title: "Feedback",
    body: "Outcomes are measured against the metrics established in the prescription. The operating method is adapted based on what the data reveals — operational excellence is not a destination but a discipline.",
    accent: "amber-gold",
  },
];

const IDEAL_CLIENTS = [
  {
    icon: Crown,
    title: "Governments & Sovereign Institutions",
    body: "National, state, and local government bodies facing governance transformation, operational modernization, or strategic challenges at national scale.",
    accent: "red",
  },
  {
    icon: Award,
    title: "Fortune-Scale Corporations",
    body: "Global corporations — technology, financial, energy, pharmaceutical — facing execution gaps, operational complexity, or the need for operational excellence systems.",
    accent: "purple",
  },
  {
    icon: Target,
    title: "Founders & C-Suite Executives",
    body: "Leaders scaling organizations, navigating transition, or seeking to implement the operating method within their leadership teams.",
    accent: "cyan",
  },
  {
    icon: Mic,
    title: "Conference & Event Organizers",
    body: "Organizers of leadership conferences, corporate events, industry summits, and private gatherings seeking a speaker with genuine operational authority.",
    accent: "emerald",
  },
  {
    icon: Heart,
    title: "Women's Leadership Organizations",
    body: "Organizations advocating for women in leadership seeking the operational evidence — the 70% data, the verification, the implementation method.",
    accent: "amber-gold",
  },
  {
    icon: GraduationCap,
    title: "Universities & Academic Institutions",
    body: "Business schools, medical schools, public policy schools, and executive education programs seeking cross-domain expertise grounded in verified experience.",
    accent: "denim",
  },
];

const CLIENT_GAINS = [
  { icon: Layers, title: "Operating Systems", body: "Protocols, checklists, decision frameworks, and accountability structures — mechanisms that persist beyond the engagement itself.", accent: "red" },
  { icon: Stethoscope, title: "The Medical Method", body: "A teachable, repeatable, scalable approach to leadership that no other provider at this level offers — clinical rigor as an operating system.", accent: "purple" },
  { icon: Globe2, title: "Sovereign-Scale Perspective", body: "Lessons from operations where the stakes are national and the margin for error is zero. Verified outcomes, not theoretical frameworks.", accent: "cyan" },
  { icon: Heart, title: "The 70% Operating Model", body: "Evidence-based approach to building high-performance diverse teams. A decade of operational data proving that overlooked talent outperforms.", accent: "emerald" },
  { icon: Scale, title: "Outcome Accountability", body: "Engagements measured by results, not deliverables. The same accountability that governs medical practice governs every service delivered.", accent: "amber-gold" },
  { icon: Lock, title: "Discretion", body: "National security level confidentiality governing every interaction. Zero information leakage. The structural foundation of how every engagement operates.", accent: "denim" },
];

export default function ServicesHubPage() {
  return (
    <>
      <PageHero
        icon={Crown}
        overline="Services & Engagements"
        overlineClassName="text-red"
        title={
          <>
            Engagements Designed for <span className="text-gradient-premium">Sovereign-Scale Outcomes.</span>
          </>
        }
        subtitle="A curated portfolio of ten engagement types organized across six service modalities — keynote speaking, executive advisory, board service, executive mentorship, operational consulting, writing and thought leadership, press and media commentary, corporate workshops, university lectures, and private engagements. Each grounded in a decade of sovereign-scale operations."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Services" }]}
        badge="Ten engagements. One operating method."
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
              <Sparkles className="size-3.5 text-purple" />
              <span className="text-overline text-purple">Executive Summary</span>
            </div>
            <h2 className="text-display-md mb-5">
              A decade of sovereign-scale operations, deployed as engagements.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Dr. Jyoti Kush offers a curated portfolio of ten engagement types across six modalities — each designed for a specific operational need and a specific caliber of client. From keynote speaking that delivers operational authority to the stage, to executive advisory that embeds the operating method within leadership teams, to board service that provides sovereign-grade governance oversight, every engagement is built on the same foundation: a physician&apos;s discipline, a decade of building, and a track record of verified outcomes.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The engagements are not commodities. They are curated — available to organizations whose challenges demand the full weight of sovereign-scale operational expertise. The process begins with a diagnostic conversation to understand the situation before prescribing the engagement. No engagement is prescribed without diagnosis. No scope is defined without understanding the landscape.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Organizations that engage Dr. Jyoti Kush are not hiring a consultant. They are engaging an operator whose entire career has been building and running systems where failure is not an option. The engagements span thirty-plus countries, three continents, and the full spectrum of sovereign-level clients — governments, royal families, political leaders, defense agencies, and Fortune-scale corporations.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {HEADLINE_STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${ACCENT[s.accent].bg} to-transparent border ${ACCENT[s.accent].border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-sm font-bold ${ACCENT[s.accent].text} leading-none mb-2`}>
                  {s.metric}{s.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Stethoscope className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Service Philosophy</span>
          </div>
          <h2 className="text-display-md mb-4">Six principles that govern every engagement.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The service philosophy is the operating method itself — the clinical discipline of a physician applied to the operational demands of sovereign-scale leadership. Not a consulting methodology. An operating system, forged through fifteen-plus years of building and running organizations.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PHILOSOPHY_PILLARS.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                    <p.icon className="size-6 text-white" />
                  </div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{p.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Layers className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Six Service Modalities</span>
          </div>
          <h2 className="text-display-md mb-4">A portfolio organized as a system.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each modality serves a distinct operational need. Together, they form a continuous engagement lifecycle where speaking introduces the method, advisory implements it, mentorship personalizes it, workshops operationalize it, and consulting transforms entire systems.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {MODALITIES.map((m, i) => {
            const a = ACCENT[m.accent];
            return (
              <motion.div
                key={m.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`size-14 rounded-2xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                    <m.icon className="size-7 text-white" />
                  </div>
                  <h3 className={`text-heading-lg mb-3 ${a.text}`}>{m.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">{m.body}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {m.services.map((s) => (
                      <span key={s} className={`text-caption px-2 py-1 rounded-full ${a.border} border bg-background/40`}>
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Compass className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Ten Engagement Types</span>
          </div>
          <h2 className="text-display-md mb-4">The complete engagement portfolio.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each engagement type addresses a specific operational need and a specific caliber of client. Every type is grounded in the same clinical method and the same outcome accountability. Explore each type for full details.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ENGAGEMENT_TYPES.map((e, i) => {
            const a = ACCENT[e.accent];
            return (
              <motion.div
                key={e.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
              >
                <Link href={e.href} className="block h-full group">
                  <div className={`relative h-full glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}>
                    <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                    <div className="relative flex flex-col h-full">
                      <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center mb-3 shadow-lg`}>
                        <e.icon className="size-5 text-white" />
                      </div>
                      <h3 className={`text-heading-md mb-2 ${a.text}`}>{e.title}</h3>
                      <p className="text-caption text-muted-foreground leading-relaxed mb-4 flex-1">{e.body}</p>
                      <div className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                        Explore Engagement <ArrowRight className="size-3" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <GitBranch className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Engagement Models</span>
          </div>
          <h2 className="text-display-md mb-4">How services are structured.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every engagement follows a structured model designed to match the nature of the challenge, the duration of the relationship, and the operational objectives. All models begin with a diagnostic conversation. No engagement is prescribed without diagnosis.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ENGAGEMENT_MODELS.map((m, i) => {
            const a = ACCENT[m.accent];
            return (
              <motion.div
                key={m.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-4">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg ring-2 ${a.ring}`}>
                    <span className="text-body-md font-bold text-white">{m.num}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{m.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{m.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-4`}>
            <TrendingUp className="size-3.5 text-lime" />
            <span className="text-overline text-lime">The Process</span>
          </div>
          <h2 className="text-display-md mb-4">From inquiry to verified outcome.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every engagement follows a five-stage process — the clinical method applied to the engagement lifecycle. The process ensures every engagement begins with diagnosis, proceeds with precision, and concludes with verified outcomes.
          </p>
        </div>
        <div className="space-y-3">
          {PROCESS_STAGES.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`group glass-card rounded-2xl p-5 lg:p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr] gap-5 items-center">
                  <div className={`size-14 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg ring-2 ${a.ring} group-hover:scale-110 group-hover:rotate-3 transition-all`}>
                    <span className="text-body-md font-bold text-white">{s.num}</span>
                  </div>
                  <div>
                    <span className={`text-overline ${a.text}`}>Stage {s.num}</span>
                    <h3 className={`text-heading-lg mb-2 ${a.text}`}>{s.title}</h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Crown className="size-3.5 text-coral" />
            <span className="text-overline text-coral">Ideal Target Clients</span>
          </div>
          <h2 className="text-display-md mb-4">Who benefits from these engagements.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The engagements are designed for organizations and leaders whose challenges demand sovereign-scale operational expertise. Each client category is served by specific engagement types and specific service configurations.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {IDEAL_CLIENTS.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <c.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{c.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{c.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Award className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">What Clients Gain</span>
          </div>
          <h2 className="text-display-md mb-4">The value of engaging the operating method.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six structural deliverables — each grounded in verified operational data, each producing outcomes that persist beyond the engagement itself. These are the mechanisms that transform how organizations execute, govern, and perform.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CLIENT_GAINS.map((g, i) => {
            const a = ACCENT[g.accent];
            return (
              <motion.div
                key={g.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <g.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{g.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{g.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-red mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage the operating method.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Every engagement begins with a diagnostic conversation — the clinical method applied to the engagement itself. The conversation determines scope, format, terms, and the verified outcomes that both parties require.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/expertise/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Brain className="size-4" />
                Explore Expertise
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Keynote Speaking", description: "Operational authority delivered from the stage. Six signature topics.", href: "/services/keynote-speaking/", icon: Mic, accent: "red" },
          { title: "Executive Advisory", description: "The operating method embedded in leadership teams and daily practice.", href: "/services/executive-advisory/", icon: Briefcase, accent: "purple" },
          { title: "Operational Consulting", description: "Sovereign-scale operational transformation, verified by outcomes.", href: "/services/operational-consulting/", icon: Activity, accent: "amber-gold" },
          { title: "Private Engagements", description: "Sovereign-level engagements by introduction only. Absolute discretion.", href: "/services/private-engagements/", icon: Lock, accent: "red" },
        ]}
      />
    </>
  );
}
